"use client";

import { useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-full bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  const filteredMenuItems = menuItems.filter(item => item.label !== "Membership" && item.label !== "Gallery");
  return <nav className="mt-8">
      <ul className="flex flex-col gap-2">
        {filteredMenuItems.slice(0, 5).map((item, i) => <li key={item.id} data-index={i}>
            <SheetClose asChild>
              <Link to={item.href || "#"} className="block px-4 py-3 text-base font-medium text-foreground hover:bg-muted-light rounded-md transition-all duration-[var(--duration-medium)]">
                {item.label}
              </Link>
            </SheetClose>
          </li>)}
        <li data-index={filteredMenuItems.slice(0, 5).length}>
          <SheetClose asChild>
            <Link to="#" className="block px-4 py-3 text-base font-medium text-primary-dark hover:bg-primary-light rounded-md transition-all duration-[var(--duration-medium)] mt-4 border border-primary/20">
              Become a member
            </Link>
          </SheetClose>
        </li>
      </ul>
    </nav>;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-sm" />
      </div>;
  }
  const filteredMenuItems = menuItems.filter(item => item.label !== "Membership" && item.label !== "Gallery");
  return <>
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" aria-label="Toggle menu">
            <Menu className="size-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle className="text-left">Navigation</SheetTitle>
          </SheetHeader>
          <MenuProvider menu_id="3">
            <MobileMenuConsumer />
          </MenuProvider>
        </SheetContent>
      </Sheet>

      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {filteredMenuItems.slice(0, 5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-foreground transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary-light after:transition-all after:duration-[var(--duration-medium)] after:ease-[var(--ease-smooth)] hover:after:w-full">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full bg-background border-b transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "h-14 shadow-medium" : "h-20"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 flex items-center gap-3">
          
          <span className="font-medium text-foreground text-lg md:text-xl lg:text-2xl">MDSA</span>
        </Link>

        <div className="flex items-center gap-6">
          <MenuProvider menu_id="3" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <Button asChild className="hidden md:inline-flex px-6 hover:!bg-primary-hover focus:!bg-primary-hover hover:scale-[1.03] hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
            <Link to="#" className="items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary shadow-xs h-9 py-2 has-[>svg]:px-3 hidden px-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] !bg-[hsl(46,97%,65%)] !text-[hsl(0,0%,0%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover hover:scale-[1.03] hover:shadow-medium disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover hover:scale-[1.03] hover:shadow-medium disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover hover:scale-[1.03] hover:shadow-medium">Become a member</Link>
          </Button>
        </div>
      </div>
    </header>;
}